import * as cookie from 'tiny-cookie';
import {cloneDeep} from 'lodash';
import Vue from 'vue';
import {AppModel} from '../models';
import {EventBus, Tracking} from '../utils';

let baseURL = process.env.API_ENDPOINT;
let baseDomain = 'api.campnerd-changer.com';
const baseDomainParts = baseURL.split('.');
const len = baseDomainParts.length;
if(len > 2) {
    baseDomain = `${baseDomainParts[len - 2]}.${baseDomainParts[len - 1]}`;
}
const backend = new URLSearchParams(window.location.search).get('backend');
if(backend && backend.match(/^[a-z0-9_-]+$/i)) {
    baseURL = `https://${backend}.${baseDomain}`;
}

baseURL = `${baseURL}/{lang}/api`;

const AUTH_TOKEN_KEY = 'AUTH_TOKEN';
const API_ENDPOINT = baseURL || 'http://localhost:8000/{lang}/api';
let COOKIE_DOMAIN = process.env.COOKIE_DOMAIN || 'localhost';
const context = new Vue();

// local functions
function _getEndpointUrl(suffix, qs = null) {
    suffix = suffix.replace(/^\/+/, '').replace(/\/+$/, '');
    suffix = `/${suffix}/`;
    if(qs !== null) {
        qs = `?${qs}`;
    } else {
        qs = '';
    }
    return API_ENDPOINT.replace('{lang}', Vue.config.lang) + suffix + qs;
}

function _fetch(what, eventStart, eventDone, options = null, callback = null, performanceTrackerId = null) {
    return new Promise(function(resolve, reject) {
        setTimeout(() => {
            const url = _getEndpointUrl(what);
            if(eventStart) {
                EventBus.$emit(eventStart, {what: what, url: url});
            }
            Tracking.trackPerformanceStart(performanceTrackerId || what);
            return context.$http.get(
                url,
                options,
            ).then(response => {
                // fetch data success
                Tracking.trackPerformanceEnd(performanceTrackerId || what, 'get');
                callback && callback(response.body);
                if(eventDone) {
                    EventBus.$emit(eventDone, {success: true, what: what, body: response.body});
                }
                resolve(response);
                return response;
            }, response => {
                // fetch data failed
                console.error(response);
                Tracking.canceltrackPerformance(performanceTrackerId || what);
                callback && callback(null);
                if(eventDone) {
                    EventBus.$emit(eventDone, {success: false, what: what, body: response.body});
                }
                reject(response);
                return response;
            });
        }, 0);
    });
}

function _add(what, eventStart, eventDone, options = null, callback = null, qs = null) {
    const url = _getEndpointUrl(what, qs);
    if(eventStart) {
        EventBus.$emit(eventStart, {swhat: what, url: url});
    }
    Tracking.trackPerformanceStart(what);
    return context.$http.post(
        url,
        options,
    ).then(response => {
        Tracking.trackPerformanceEnd(what, 'post');
        // adding data success
        callback && callback(response.body);
        if(eventDone) {
            EventBus.$emit(eventDone, {success: true, what: what, body: response.body});
        }
        return response;
    }, response => {
        // adding data failed
        Tracking.canceltrackPerformance(what);
        console.error(response);
        callback && callback(null);
        if(eventDone) {
            EventBus.$emit(eventDone, {success: false, what: what, body: response.body});
        }
        return response;
    });
}

function _update(what, eventStart, eventDone, options = null, callback = null) {
    const url = _getEndpointUrl(what) + options.id + '/';
    if(eventStart) {
        EventBus.$emit(eventStart, {swhat: what, url: url});
    }
    Tracking.trackPerformanceStart(what);
    return context.$http.put(
        url,
        options,
    ).then(response => {
        // update data success
        Tracking.trackPerformanceEnd(what, 'put');
        callback && callback(response.body);
        if(eventDone) {
            EventBus.$emit(eventDone, {success: true, what: what, body: response.body});
        }
        return response;
    }, response => {
        Tracking.canceltrackPerformance(what);
        // update data failed
        console.error(response);
        callback && callback(null);
        if(eventDone) {
            EventBus.$emit(eventDone, {success: false, what: what, body: response.body});
        }
        return response;
    });
}

function _delete(what, eventStart, eventDone, options = null, callback = null) {
    let url = _getEndpointUrl(what) + options.id + '/';

    if(options.query) {
        url += '?' + options.query;
    }

    if(eventStart) {
        EventBus.$emit(eventStart, {swhat: what, url: url});
    }
    Tracking.trackPerformanceStart(what);
    return context.$http.delete(
        url,
        options,
    ).then(response => {
        Tracking.trackPerformanceEnd(what, 'delete');
        // update data success
        callback && callback(response.body);
        if(eventDone) {
            EventBus.$emit(eventDone, {success: true, what: what, body: response.body});
        }
        return response;
    }, response => {
        Tracking.canceltrackPerformance(what);
        // update data failed
        console.error(response);
        callback && callback(null);
        if(eventDone) {
            EventBus.$emit(eventDone, {success: false, what: what, body: response.body});
        }
        return response;
    });
}

// public functions
const user = {
    login(email, password) {
        EventBus.$emit(EventBus.LOGIN_START);
        const existingToken = this.getToken();
        if(existingToken) {
            EventBus.$emit(EventBus.LOGIN_DONE, {success: true, token: existingToken});
            return;
        }

        Tracking.trackPerformanceStart('login');
        context.$http.post(
            _getEndpointUrl('token-auth'), {
                email: email,
                password: password,
            }
        ).then(response => {
            if(response.ok && response.body.token) {
                this.setToken(response.body.token);
                EventBus.$emit(EventBus.LOGIN_DONE, {success: true, token: response.body.token});
            } else {
                EventBus.$emit(EventBus.LOGIN_DONE, {success: false});
            }
            Tracking.trackPerformanceEnd('login', 'post');
        }, response => {
            Tracking.trackPerformanceEnd('login', 'post');
            console.error(response);
            EventBus.$emit(EventBus.LOGIN_DONE, {success: false});
        });
    },
    logout() {
        this.deleteToken();
        EventBus.$emit(EventBus.LOGOUT_DONE);
    },
    loggedIn() {
        return !!this.getToken();
    },
    getToken() {
        return cookie.get(AUTH_TOKEN_KEY);
    },
    setToken(token) {
        if(AppModel.stayLoggedIn) {
            cookie.set(AUTH_TOKEN_KEY, token, {expires: '1M', domain: COOKIE_DOMAIN});
        } else {
            cookie.set(AUTH_TOKEN_KEY, token, {domain: COOKIE_DOMAIN});
        }
    },
    deleteToken() {
        cookie.remove(AUTH_TOKEN_KEY, {domain: COOKIE_DOMAIN});
    },
    fetchUser() {
        return _fetch('account', EventBus.FETCH_USER_START, EventBus.FETCH_USER_DONE);
    },
    updateUser(settings) {
        const what = 'account';
        const url = _getEndpointUrl(what);

        EventBus.$emit(EventBus.UPDATE_USER_START, {what: what, url: url});

        Tracking.trackPerformanceStart(what);
        return context.$http.put(
            url,
            settings,
        ).then(response => {
            Tracking.trackPerformanceEnd(what, 'put');
            // update data success
            EventBus.$emit(
                EventBus.UPDATE_USER_DONE,
                {success: true, what: what, body: response.body}
            );
            return response;
        }, response => {
            // update data failed
            Tracking.canceltrackPerformance(what);
            console.error(response);
            EventBus.$emit(
                EventBus.UPDATE_USER_DONE,
                {success: false, what: what, body: response.body}
            );
            return response;
        });
    },
};

const seasons = {
    fetchSeasons() {
        // always fetch seasons based on active campground
        let options = {
            params: {
                'campground': AppModel.activeCampground.slug
            }
        };
        return _fetch('season', EventBus.FETCH_SEASONS_START, EventBus.FETCH_SEASONS_DONE, options);
    },
    addSeason(options) {
        return _add('season', EventBus.ADD_SEASONS_START, EventBus.ADD_SEASONS_DONE, options);
    },
    updateSeason(options) {
        return _update('season', EventBus.UPDATE_SEASONS_START, EventBus.UPDATE_SEASONS_DONE, options);
    },
    deleteSeason(options) {
        return _delete('season', EventBus.UPDATE_SEASONS_START, EventBus.UPDATE_SEASONS_DONE, options);
    },
    orderSeason(options) {
        return _add('/set-order/season/', EventBus.UPDATE_SEASONS_START, EventBus.UPDATE_SEASONS_DONE, options);
    }
};

const bookings = {
    fetchBookings(filters = null) {
        if(!AppModel.activeCampground) {
            setTimeout(() => {
                this.fetchBookings(filters);
            }, 10);
            return;
        }

        if(!filters) {
            filters = {};
        }
        filters['campground'] = AppModel.activeCampground.slug;
        let options = {
            params: filters,
        };
        _fetch('booking', EventBus.FETCH_BOOKINGS_START, EventBus.FETCH_BOOKINGS_DONE, options);
    },

    fetchBookingDetail(bookingId) {
        return _fetch(`booking/${bookingId}`, EventBus.FETCH_BOOKING_DETAIL_START, EventBus.FETCH_BOOKING_DETAIL_DONE, null, null, 'booking');
    },

    updateBookingDetail(bookingId, options) {
        Tracking.trackPerformanceStart('booking');
        const url = _getEndpointUrl(`booking/${bookingId}`);
        return context.$http.put(
            url,
            options,
        ).then(response => {
            // update data success
            Tracking.trackPerformanceEnd('booking', 'put');
            return response;
        }, response => {
            // update data failed
            console.error(response);
            Tracking.canceltrackPerformance('booking');
            return response;
        });
    },

    updateBookingProcess(bookingId, data) {
        return _fetch(`booking/update-process/${bookingId}`, null, null, {params: data}, null, 'booking/update-process');
    },
    setPmsConfirmedAt(bookingId) {
        console.log('gk-debug', 'setPmsConfirmedAt', bookingId);
        return _fetch(`booking/set-pms-confirmed-at/${bookingId}`, null, null, null, null, 'booking/set-pms-confirmed-at');
    },
};

const resources = {
    fetchResources() {
        return _fetch(`pricelist_resources/${AppModel.activeCampground.slug}`, null, null, null);
    },
    fetchPmsPrices() {
        return _fetch(`fetch_pms_prices/${AppModel.activeCampground.slug}`, null, null, null);
    },
    fetchPmsAvailabilities() {
        return _fetch(`fetch_pms_availabilities/${AppModel.activeCampground.slug}`, null, null, null);
    }
};

const campsites = {
    fetchCampsites() {
        // always fetch seasons based on active campground
        let options = {
            params: {
                'campground': AppModel.activeCampground.slug
            }
        };
        return _fetch('campsite', null, null, options);
    },
    addCampsite(campsite) {
        let campsiteCopy = Object.assign({}, cloneDeep(campsite));
        // normalize target_types for backend
        if(Array.isArray(campsiteCopy.target_types)) {
            campsiteCopy.target_types = campsiteCopy.target_types.map(function(tt) {
                // accept either { target_type: { id } } or { target_type_id }
                const id = tt && tt.target_type && tt.target_type.id != null
                    ? Number(tt.target_type.id)
                    : (tt && tt.target_type_id != null ? Number(tt.target_type_id) : null);
                return {
                    target_type_id: id,
                    distance_min: tt && tt.distance_min != null ? Number(tt.distance_min) : null,
                    distance_max: tt && tt.distance_max != null ? Number(tt.distance_max) : null,
                    walk_time_min: tt && tt.walk_time_min != null ? Number(tt.walk_time_min) : null,
                    walk_time_max: tt && tt.walk_time_max != null ? Number(tt.walk_time_max) : null,
                    accessible: !!(tt && tt.accessible),
                    notes: tt && tt.notes ? tt.notes : ''
                };
            }).filter(function(row) {
                // drop invalid entries without id
                return row.target_type_id != null;
            });
        }
        campsiteCopy.arrival_weekdays = [];
        campsiteCopy.departure_weekdays = [];
        Object.values(campsite.arrival_weekdays).forEach((isActive, day) => {
            if(isActive) {
                campsiteCopy.arrival_weekdays.push(day);
            }
        });
        Object.values(campsite.departure_weekdays).forEach((isActive, day) => {
            if(isActive) {
                campsiteCopy.departure_weekdays.push(day);
            }
        });
        return _add('campsite', null, null, campsiteCopy);
    },
    updateCampsite(campsite) {
        let campsiteCopy = Object.assign({}, cloneDeep(campsite));
        // normalize target_types for backend
        if(Array.isArray(campsiteCopy.target_types)) {
            campsiteCopy.target_types = campsiteCopy.target_types.map(function(tt) {
                // accept either { target_type: { id } } or { target_type_id }
                const id = tt && tt.target_type && tt.target_type.id != null
                    ? Number(tt.target_type.id)
                    : (tt && tt.target_type_id != null ? Number(tt.target_type_id) : null);
                return {
                    target_type_id: id,
                    distance_min: tt && tt.distance_min != null ? Number(tt.distance_min) : null,
                    distance_max: tt && tt.distance_max != null ? Number(tt.distance_max) : null,
                    walk_time_min: tt && tt.walk_time_min != null ? Number(tt.walk_time_min) : null,
                    walk_time_max: tt && tt.walk_time_max != null ? Number(tt.walk_time_max) : null,
                    accessible: !!(tt && tt.accessible),
                    notes: tt && tt.notes ? tt.notes : ''
                };
            }).filter(function(row) {
                // drop invalid entries without id
                return row.target_type_id != null;
            });
        }
        campsiteCopy.arrival_weekdays = [];
        campsiteCopy.departure_weekdays = [];
        Object.values(campsite.arrival_weekdays).forEach((isActive, day) => {
            if(isActive) {
                campsiteCopy.arrival_weekdays.push(day);
            }
        });
        Object.values(campsite.departure_weekdays).forEach((isActive, day) => {
            if(isActive) {
                campsiteCopy.departure_weekdays.push(day);
            }
        });
        return _update('campsite', null, null, campsiteCopy);
    },
    deleteCampsite(options) {
        return _delete('campsite', null, null, options);
    },
    oderCampsite(options) {
        return _add('/set-order/campsite/', null, null, options);
    },
    copyCampsite(id) {
        return _add(`copy-accommodation-type/${AppModel.activeCampground.slug}/${id}/`, null, null, null);
    }
};

const settings = {
    updateDocument(data) {
        return context.$http.post(_getEndpointUrl(`campground-documents/${AppModel.activeCampground.slug}`), data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(response => {
            console.log(response);
        });
    },
    fetchDocuments() {
        return _fetch(`campground-documents/${AppModel.activeCampground.slug}`, null, null, null, null, 'campground-documents');
    },
    fetchSettings() {
        return _fetch(`campground-settings/${AppModel.activeCampground.slug}`, null, null, null, null, 'campground-settings');
    },
    updateSettings(settings) {
        const what = 'campground-settings';
        const url = _getEndpointUrl(what) + `${AppModel.activeCampground.slug}/`;

        EventBus.$emit(EventBus.UPDATE_SETTINGS_START, {what: what, url: url});

        Tracking.trackPerformanceStart(what);
        return context.$http.put(
            url,
            settings,
        ).then(response => {
            Tracking.trackPerformanceEnd(what, 'put');
            // update data success
            EventBus.$emit(
                EventBus.UPDATE_SETTINGS_DONE,
                {success: true, what: what, body: response.body}
            );
            return response;
        }, response => {
            // update data failed
            Tracking.canceltrackPerformance(what);
            console.error(response);
            EventBus.$emit(
                EventBus.UPDATE_SETTINGS_DONE,
                {success: false, what: what, body: response.body}
            );
            return response;
        });
    },
};

const persons = {
    fetchPersons() {
        // always fetch seasons based on active campground
        let options = {
            params: {
                'campground': AppModel.activeCampground.slug
            }
        };
        return _fetch('person', null, null, options);
    },
    addPerson(options) {
        return _add('person', null, null, options);
    },
    updatePerson(options) {
        return _update('person', null, null, options);
    },
    deletePerson(options) {
        return _delete('person', null, null, options);
    },
    oderPerson(options) {
        return _add('/set-order/person/', null, null, options);
    }
};

const policies = {
    fetchPolicies() {
        // always fetch seasons based on active campground
        let options = {
            params: {
                'campground': AppModel.activeCampground.slug
            }
        };
        return _fetch('cancellation-terms', null, null, options);
    },
    addPolicy(options) {
        return _add('cancellation-terms', null, null, options);
    },
    updatePolicy(options) {
        return _update('cancellation-terms', null, null, options);
    },
    deletePolicy(options) {
        return _delete('cancellation-terms', null, null, options);
    },
};

const extras = {
    fetchExtras(params) {
        if(!params) {
            params = {};
        }
        params['campground'] = AppModel.activeCampground.slug;
        let options = {params: params};
        return _fetch('extras', null, null, options);
    },
    addExtra(options) {
        return _add('extras', null, null, options);
    },
    updateExtra(options) {
        return _update('extras', null, null, options);
    },
    deleteExtra(options) {
        return _delete('extras', null, null, options);
    },
    oderExtras(options) {
        return _add('/set-order/extra/', null, null, options);
    }
};

const availabilities = {
    fetchAvailabilities(filters = null) {
        if(!filters) {
            filters = {};
        }
        let options = {
            params: filters,
        };
        return _fetch(`availabilities/${AppModel.activeCampground.slug}/`, EventBus.FETCH_AVAILABILITIES_START, EventBus.FETCH_AVAILABILITIES_DONE, options);
    },
    updateAvailabilities(availabilities) {
        const what = `availabilities/${AppModel.activeCampground.slug}/`;
        const url = _getEndpointUrl(what);
        Tracking.trackPerformanceStart(what);
        return context.$http.put(
            url, {availabilities: availabilities}
        ).then(response => {
            // update data success
            Tracking.trackPerformanceEnd(what, 'put');
            return response;
        }, response => {
            Tracking.canceltrackPerformance(what);
            console.error(response);
            return response;
        });
    },
    fetchChangedAvailabilities(page = 1, ordering = 'day', filters = null) {
        let options = {
            params: {
                page: page,
                ordering: ordering,
            },
        };

        if(filters) {
            if(filters.campsite !== null && filters.campsite !== undefined) {
                options.params['campsite'] = filters.campsite;
            }
            if(filters.date_from !== null && filters.date_from !== undefined) {
                options.params['date_from'] = filters.date_from;
            }
            if(filters.date_until !== null && filters.date_until !== undefined) {
                options.params['date_until'] = filters.date_until;
            }
        }

        return _fetch(`availabilities/${AppModel.activeCampground.slug}/changed/`, EventBus.FETCH_CHANGED_AVAILABILITIES_START, EventBus.FETCH_CHANGED_AVAILABILITIES_DONE, options);
    },
};

const documents = {
    fetch() {
        return _fetch(`legal-documents/${AppModel.activeCampground.slug}`, null, null);
    },
    submit(campground, id, data) {
        const url = _getEndpointUrl(`accept_legal_document/${campground}/${id}`);

        return context.$http.post(
            url,
        );
    },
};

const portals = {
    fetch() {
        return _fetch(`portal-campground-infos/${AppModel.activeCampground.slug}`);
    },
    update(data) {
        return _add(`portal-campground-infos/${AppModel.activeCampground.slug}`, null, null, data);
    },
};

const portalMembers = {
    fetch() {
        return _fetch('portal-campground-member');
    },
    create(data) {
        return _add('portal-campground-member', null, EventBus.FETCH_CHANGED_AVAILABILITIES_DONE, data);
    },
    update(id, data) {
        const options = {
            id,
            ...data,
        };

        return _update('portal-campground-member', null, null, options);
    },
    changeState(id, data) {
        return _add(`portal-campground-member-change-state/${id}`, null, EventBus.FETCH_CHANGED_AVAILABILITIES_DONE, data);
    },
};

const translations = {
    fetch(params) {
        let options = {
            params: {
                src: params.source_language,
                dest: params.target_language,
            }
        };
        return _fetch(`translations/${AppModel.activeCampground.slug}`, null, null, options, null, null);
    },
    save(targetLanguage, translationData) {
        return _add(`translations/${AppModel.activeCampground.slug}`, null, null, translationData, null, `dest=${targetLanguage}`);
    },
};

const payment = {
    save(data) {
        const url = _getEndpointUrl(`payment-data/${AppModel.activeCampground.slug}/`);
        return context.$http.post(
            url,
            data,
        );
    },
    getStripeOnboardingUrl(express) {
        // Erwartet vom Backend eine URL im Response-Body (z.B. { url: '...' })
        const options = {
            params: {
                express: express ? 1 : 0,
            },
        };
        return _fetch(`payments/stripe/stripe-onboarding/${AppModel.activeCampground.slug}`, null, null, options);
    },
    getStripeDashboardUrl() {
        // Erwartet vom Backend eine URL im Response-Body (z.B. { url: '...' })
        return _fetch(`stripe-dashboard/${AppModel.activeCampground.slug}`);
    },
    disconnectStripe() {
        // Trennt die Stripe-Verbindung des aktiven Campgrounds
        // Laut Vorgabe: POST auf stripe-disconnect
        return _add(`payments/stripe/stripe-disconnect/${AppModel.activeCampground.slug}`);
    },
};

const payouts = {
    fetch() {
        const url = _getEndpointUrl(`payouts/${AppModel.activeCampground.slug}/`);
        return context.$http.get(url);
    },
};

const invoices = {
    fetch(query) {
        if(!query) {
            query = {
                'page': 1,
            };
        }
        let options = {
            params: query,
        };
        options.params.campground = AppModel.activeCampground.slug;

        return _fetch('invoice', null, null, options);
    },
};

const campgroundImages = {
    fetchImages(imageType, images) {
        return _fetch(`campground-images/${AppModel.activeCampground.slug}/${imageType}`, null, null, null);
    },
    addImages(imageType, images) {
        return _add(`campground-images/${AppModel.activeCampground.slug}/${imageType}`, null, null, images, null, null);
    },
    deleteImages(id) {
        const options = {
            id,
        };

        return _delete(`campground-images/${AppModel.activeCampground.slug}`, null, null, options);
    },
};

const campsiteImages = {
    addImages(options) {
        return _add('campsite-images', null, null, options, null, null);
    },
    fetchImages(campsiteId) {
        // always fetch seasons based on active campground
        let options = {
            params: {
                'campsite': campsiteId,
            }
        };
        return _fetch('campsite-images', null, null, options);
    },
    sortImages(data) {
        let options = {
            me: data.me,
            insert_after: data.insert_after,
        };
        return _add('set-campsite-images-order/', null, null, options);
    },
    updateImage(imageId, title) {
        let options = {
            id: imageId,
            title: title,
        };
        return _update('campsite-images', null, null, options);
    },
    deleteImage(imageId, campsiteId) {
        let options = {
            id: imageId,
            query: `campsite=${campsiteId}`,
        };
        return _delete('campsite-images', null, null, options);
    },
};

const attributes = {
    getAllAccomodations() {
        return _fetch(`accommodationtyp-attributes/`, null, null, null);
    },
    getActiveAccomodations() {
        return _fetch(`accommodation-typ-attributes-entries/${AppModel.activeCampground.slug}/`, null, null, null);
    },
    setAccomodations(id, attributes) {
        const options = [{
            id,
            attributes,
        }];

        return _add(`accommodation-typ-attributes-entries/${AppModel.activeCampground.slug}/`, null, null, options);
    },
    getAllCampsites() {
        return _fetch(`campground-attributes/`, null, null, null);
    },
    getActiveCampsites() {
        return _fetch(`campground-attributes-entries/${AppModel.activeCampground.slug}/`, null, null, null);
    },
    setCampsites(attributes) {
        const options = {
            attributes,
        };

        return _add(`campground-attributes-entries/${AppModel.activeCampground.slug}/`, null, null, options);
    },
};

const suitables = {
    getAllSuitables() {
        return _fetch(`accommodationtyp-suitables/`, null, null, null);
    },
    getActiveSuitables() {
        return _fetch(`accommodation-typ-suitable-entries/${AppModel.activeCampground.slug}/`, null, null, null);
    },
    setSuitablesForCampsite(id, suitables) {
        const options = [{
            id,
            suitables,
        }];

        return _add(`accommodation-typ-suitable-entries/${AppModel.activeCampground.slug}/`, null, null, options);
    },
};

// PmsAccommodations API
const pmsAccommodations = {
    fetchPmsAccommodations(params = {}) {
        const query = {
            campground: AppModel.activeCampground.slug,
        };
        // allow optional pagination and filters
        if(typeof params.page !== 'undefined' && params.page !== null) {
            query.page = params.page;
        }
        if(typeof params.page_size !== 'undefined' && params.page_size !== null) {
            query.page_size = params.page_size;
        }
        if(typeof params.q !== 'undefined' && params.q !== null && params.q !== '') {
            query.q = params.q;
        }
        if(typeof params.active !== 'undefined' && params.active !== null && params.active !== '') {
            // expect true/false or '1'/'0'
            query.active = params.active;
        }
        if(typeof params.campsite !== 'undefined' && params.campsite !== null && params.campsite !== '') {
            query.campsite = params.campsite;
        }
        // optional ordering (Django-style: field or -field)
        if(typeof params.ordering !== 'undefined' && params.ordering !== null && params.ordering !== '') {
            query.ordering = params.ordering;
        }
        // optional filter: only items without assigned accommodation type/campsite
        if(typeof params.unassigned_only !== 'undefined' && params.unassigned_only !== null && params.unassigned_only !== '') {
            query.unassigned_only = params.unassigned_only;
        }
        // optional filter: only items WITH assignment
        if(typeof params.assigned_only !== 'undefined' && params.assigned_only !== null && params.assigned_only !== '') {
            query.assigned_only = params.assigned_only;
        }
        const options = { params: query };
        return _fetch('pms-accommodations', null, null, options);
    },
    fetchPmsAccommodationDetail(id) {
        return _fetch(`pms-accommodations/${id}`, null, null, null);
    },
    addPmsAccommodation(item) {
        const copy = Object.assign({}, cloneDeep(item));
        // ensure accommodation_types only contains IDs and remove legacy fields
        if(Array.isArray(copy.accommodation_types)) {
            copy.accommodation_types = copy.accommodation_types.map(t => typeof t === 'object' ? t.id : t);
        }
        // ensure attributes and suitables only contain IDs
        if(Array.isArray(copy.attributes)) {
            copy.attributes = copy.attributes.map(a => typeof a === 'object' ? a.id : a);
        }
        if(Array.isArray(copy.suitables)) {
            copy.suitables = copy.suitables.map(s => typeof s === 'object' ? s.id : s);
        }
        delete copy.accommodation_typ;
        delete copy.campsite_ids;

        // sanitize translations: remove accidental 'undefined' key and ensure active language exists
        if(!copy.translations) { copy.translations = {}; }
        var effectiveLang = (AppModel && AppModel.lang) || (Vue && Vue.config && Vue.config.lang) || 'de';
        if(typeof copy.translations.undefined !== 'undefined') { try { delete copy.translations.undefined; } catch (e) { } }
        if(!copy.translations[effectiveLang]) { copy.translations[effectiveLang] = { name: copy.name || '', description: copy.description || '' }; }
        // ensure current language flat fields exist for non-parler REST backends (mirror from effective language)
        if(copy && copy.translations && copy.translations[effectiveLang]) {
            if(typeof copy.name === 'undefined') { copy.name = copy.translations[effectiveLang].name; }
            if(typeof copy.description === 'undefined') { copy.description = copy.translations[effectiveLang].description; }
        }
        // send campground via querystring if required by backend
        // use _add querystring parameter to avoid malformed URLs with trailing slash after '?'
        return _add('pms-accommodations', null, null, copy, null, `campground=${AppModel.activeCampground.slug}`);
    },
    updatePmsAccommodation(item) {
        const copy = Object.assign({}, cloneDeep(item));
        // ensure accommodation_types only contains IDs and remove legacy fields
        if(Array.isArray(copy.accommodation_types)) {
            copy.accommodation_types = copy.accommodation_types.map(t => typeof t === 'object' ? t.id : t);
        }
        // ensure attributes and suitables only contain IDs
        if(Array.isArray(copy.attributes)) {
            copy.attributes = copy.attributes.map(a => typeof a === 'object' ? a.id : a);
        }
        if(Array.isArray(copy.suitables)) {
            copy.suitables = copy.suitables.map(s => typeof s === 'object' ? s.id : s);
        }
        delete copy.accommodation_typ;
        delete copy.campsite_ids;

        // ensure current language flat fields exist for non-parler REST backends
        if(copy && copy.translations && copy.translations[AppModel.lang]) {
            if(typeof copy.name === 'undefined') { copy.name = copy.translations[AppModel.lang].name; }
            if(typeof copy.description === 'undefined') { copy.description = copy.translations[AppModel.lang].description; }
        }
        // updates go to clean resource path; campground is not required for detail updates
        return _update('pms-accommodations', null, null, copy);
    },
    deletePmsAccommodation(options) {
        return _delete('pms-accommodations', null, null, options);
    },
};

const dayPrices = {
    getDayPrices(accommodationId = null, startDate, endDate) {
        const params = {
            'start': startDate,
            'end': endDate
        };

        if(accommodationId) {
            params['acco_id'] = accommodationId;
        }

        return _fetch(`dayprices/${AppModel.activeCampground.slug}`, null, null, {params: params});
    },
    setDayPrices(data) {
        return _add(`dayprices/${AppModel.activeCampground.slug}/`, null, null, data);
    },
    getDayPricesTable(resource) {
        const params = {
            'resource': resource
        };
        return _fetch(`dayprices-table/${AppModel.activeCampground.slug}`, null, null, {params: params});
    },
    downloadExcel() {
        return _fetch(`dayprices/excel/${AppModel.activeCampground.slug}/`, null, null, {
            responseType: 'blob',
        });
    },
    uploadExcel(data) {
        return context.$http.post(_getEndpointUrl(`dayprices/excel/${AppModel.activeCampground.slug}`), data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },
};

const lockTimes = {
    getLockTimes() {
        let options = {
            params: {
                'campground': AppModel.activeCampground.slug
            }
        };
        return _fetch(`campground-locktimes`, null, null, options);
    },
    createLockTime(data) {
        return _add(`campground-locktimes`, null, null, data);
    },
    changeLockTime(data) {
        return _update('campground-locktimes', null, null, data);
    },
    deleteLockTime(id) {
        return _delete(`campground-locktimes`, null, null, {
            id,
        });
    },
};

const knowledgebase = {
    _slug() {
        return AppModel.activeCampground.slug;
    },
    fetchTopics() {
        return _fetch(`knowledgebase/${this._slug()}/topics`);
    },
    createTopic(data) {
        return _add(`knowledgebase/${this._slug()}/topics`, null, null, data);
    },
    updateTopic(id, data) {
        const url = _getEndpointUrl(`knowledgebase/${this._slug()}/topics`) + id + '/';
        return context.$http.patch(url, data);
    },
    deleteTopic(id) {
        return _delete(`knowledgebase/${this._slug()}/topics`, null, null, {id});
    },
    discardTopicDraft(id) {
        return _add(`knowledgebase/${this._slug()}/topics/${id}/discard-draft`, null, null, {});
    },
    restoreTopic(id) {
        return _add(`knowledgebase/${this._slug()}/topics/${id}/restore`, null, null, {});
    },
    fetchArticles(topicId = null) {
        const options = topicId ? {params: {topic: topicId}} : null;
        return _fetch(`knowledgebase/${this._slug()}/articles`, null, null, options);
    },
    fetchArticle(id) {
        return _fetch(`knowledgebase/${this._slug()}/articles/${id}`);
    },
    createArticle(data) {
        return _add(`knowledgebase/${this._slug()}/articles`, null, null, data);
    },
    updateArticle(id, data) {
        const url = _getEndpointUrl(`knowledgebase/${this._slug()}/articles`) + id + '/';
        return context.$http.patch(url, data);
    },
    deleteArticle(id) {
        return _delete(`knowledgebase/${this._slug()}/articles`, null, null, {id});
    },
    discardArticleDraft(id) {
        return _add(`knowledgebase/${this._slug()}/articles/${id}/discard-draft`, null, null, {});
    },
    restoreArticle(id) {
        return _add(`knowledgebase/${this._slug()}/articles/${id}/restore`, null, null, {});
    },
    createVariant(data) {
        return _add(`knowledgebase/${this._slug()}/variants`, null, null, data);
    },
    updateVariant(id, data) {
        const url = _getEndpointUrl(`knowledgebase/${this._slug()}/variants`) + id + '/';
        return context.$http.patch(url, data);
    },
    deleteVariant(id) {
        return _delete(`knowledgebase/${this._slug()}/variants`, null, null, {id});
    },
    discardVariantDraft(id) {
        return _add(`knowledgebase/${this._slug()}/variants/${id}/discard-draft`, null, null, {});
    },
    restoreVariant(id) {
        return _add(`knowledgebase/${this._slug()}/variants/${id}/restore`, null, null, {});
    },
    publishAll() {
        return _add(`knowledgebase/${this._slug()}/publish-all`, null, null, {});
    },
    discardAll() {
        return _add(`knowledgebase/${this._slug()}/discard-all`, null, null, {});
    },
    planImport(document, instructions) {
        // Step 1: synchronously propose an editable topic/article-title tree
        // from the document. `instructions` is optional operator steering.
        // Returns {structure: [{title, articles:[...]}, ...]}.
        return _add(`knowledgebase/${this._slug()}/import/plan`, null, null, {document, instructions});
    },
    importDocument(document, structure, instructions) {
        // Step 2: kicks off an async LLM import; returns 202 immediately. The
        // drafts appear once the background job finishes — the user refreshes
        // to see them. `structure` is the (edited) tree from step 1,
        // `instructions` is optional operator steering for the content.
        return _add(`knowledgebase/${this._slug()}/import`, null, null, {document, structure, instructions});
    },
    fetchChannels() {
        return _fetch('knowledgebase/channels');
    },
    fetchConnections() {
        return _fetch(`knowledgebase/${this._slug()}/connections`);
    },
    createConnection(data) {
        return _add(`knowledgebase/${this._slug()}/connections`, null, null, data);
    },
    updateConnection(id, data) {
        const url = _getEndpointUrl(`knowledgebase/${this._slug()}/connections`) + id + '/';
        return context.$http.patch(url, data);
    },
    deleteConnection(id) {
        return _delete(`knowledgebase/${this._slug()}/connections`, null, null, {id});
    },
    resyncConnection(id) {
        return _add(`knowledgebase/${this._slug()}/connections/${id}/resync`, null, null, {});
    },
    rotateApiKey(id) {
        return _add(`knowledgebase/${this._slug()}/connections/${id}/rotate-key`, null, null, {});
    },
    setArticleVisibility(articleId, connectionId, visible) {
        // Backend collapses the 4×2 toggle state machine — the frontend just
        // says "make this visible" or "hide this", per-connection.
        return _add(
            `knowledgebase/${this._slug()}/articles/${articleId}/set-visibility`,
            null, null,
            {connection_id: connectionId, visible},
        );
    },
};

// exposed api
const Api = {
    attributes,
    suitables,
    dayPrices,
    lockTimes,
    knowledgebase,
    bookings: bookings,
    pmsAccommodations: pmsAccommodations,
    // transient draft for prefilled "new" PmsAccommodation (not persisted)
    pmsDraft: null,
    bootstrapData: null,
    nextBootstrapTimeout: null,
    bootstrap(force) {
        if(!this.bootstrapData || force) {
            if(this.currentPromise) {
                return this.currentPromise;
            }
            this.currentPromise = _fetch('bootstrap', EventBus.BOOTSTRAP_START, EventBus.BOOTSTRAP_DONE, null).then((response) => {
                this.currentPromise = false;
                this.bootstrapData = response;
                if(response.ok) {
                    // console.log('boostrap success');
                    AppModel.setOnlyDayPrices(response.body.only_day_prices);
                    AppModel.setUser(response.body.user);
                    AppModel.setFeatures(response.body.features);
                    AppModel.setChecks(response.body.checks);
                    AppModel.setBillingTypes(response.body.extras_billing_types);
                    AppModel.setExtraTypes(response.body.extra_types);
                    AppModel.setExtraSubTypes(response.body.extra_sub_types);
                    AppModel.setPersons(response.body.person_types);
                    AppModel.setLanguages(response.body.languages);
                    AppModel.setCountries(response.body.countries);
                    AppModel.setCurrencies(response.body.currencies);
                    AppModel.setMinPaymentAge(response.body.min_payment_age);
                    AppModel.setValidBankCountries(response.body.valid_bank_countries);
                    AppModel.setSettings(response.body.settings);
                    AppModel.setPortals(response.body.portals);
                    AppModel.setDepositInfo(response.body.deposit_info);
                    AppModel.setSurfaces(response.body.surfaces);
                    AppModel.setTargetTypes(response.body.target_types);
                    AppModel.setTopXAttributes(response.body.topx_attributes);
                    AppModel.setStripeConfigured(response.body.stripe_configured);

                    if(response.body.seconds_till_tomorrow) {
                        clearTimeout(this.nextBootstrapTimeout);
                        this.nextBootstrapTimeout = setTimeout(() => {
                            console.debug('new day, new bootstrap');
                            this.bootstrap(true);
                        }, Number(response.body.seconds_till_tomorrow) * 1000 + 5000);
                    }

                    if(!response.body.campgrounds.length) {
                        EventBus.$emit(EventBus.USER_HAS_NO_NO_CAMPGROUND);
                    } else {
                        AppModel.setCampgrounds(response.body.campgrounds);
                    }
                    EventBus.$emit(EventBus.BOOTSTRAP_DONE);
                } else {
                    this.bootstrapData = null;
                    console.log('bootstrap failed');
                }
                return response;
            }, (response) => {
                this.bootstrapData = null;
                console.log('bootstrap failed');
                this.currentPromise = null;
                return response;
            });
            return this.currentPromise;
        } else {
            // console.log('bootsrap from cache');
            return Promise.resolve(this.bootstrapData);
        }
    },
    resources: resources,
    campsites: campsites,
    campsiteImages: campsiteImages,
    campgroundImages,
    currentPromise: null,
    persons: persons,
    extras: extras,
    policies: policies,
    requireAuth(to, from, next) {
        if(to.params.lang) {
            AppModel.setLang(to.params.lang);
        }
        if(!this.user.loggedIn()) {
            next({
                path: '/login',
                query: {redirect: to.fullPath}
            });
        } else {
            next();
        }
    },
    seasons: seasons,
    settings: settings,
    availabilities: availabilities,
    translations: translations,
    payment: payment,
    portals,
    portalMembers,
    documents: documents,
    payouts: payouts,
    invoices: invoices,
    user: user,
    plausibility() {
        return _fetch('plausibility/' + AppModel.activeCampground.slug, null, null, null, null, 'plausibility');
    },
    setCampgroundPublished(published) {
        const url = _getEndpointUrl(`campground/${AppModel.activeCampground.slug}/`);

        const trackingId = `campground/${published ? 'publish' : 'unpublish'}`;
        Tracking.trackPerformanceStart(trackingId);

        return context.$http.put(url, {published: published}).then(response => {
            if(response.status >= 400) {
                Tracking.canceltrackPerformance(trackingId);

                return response;
            }

            // update data success
            Tracking.trackPerformanceEnd(trackingId, 'put');
            AppModel.activeCampground.published = response.body.published;
            EventBus.$emit(EventBus.CAMPGROUND_CHANGED);
            return response;
        }, response => {
            // update data failed
            Tracking.canceltrackPerformance(trackingId);
            console.error(response);
            return response;
        });
    },
};

export default Api;



// WEBPACK FOOTER //
// ./src/api/Api.js